import { useMemo } from 'react';
import {
  AccountStatus,
  CardTypesEnum,
  EnrollmentStatus,
  InvestmentPathType,
  SpendPeriodType,
  useQuery,
} from '@common-fe/common-fe';
import { CIPStatus, Enrollment, onlyDateFromServer } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

export const HSA_TYPE_KEY = 'HSA';
export const ACTIVE_ENROLLMENT_STATUS = 'ACTIVE';
export const OPEN_ACCOUNT_STATUS = 'OPEN';
export const NO_DEADLINE_LABEL = 'No deadline';




interface Params {
  employeeId?: string | number | null;
  accountId?: string | number | null;
  status?: string | null;
  enabled?: boolean;
}
export const QUERY_KEY = 'GET_ENROLLMENTS_LIST';

export const useGetEnrollments = ({ employeeId, accountId, status, enabled = true }: Params) => {
  const { isLoading, isSuccess, error, data, refetch } = useQuery(
    [QUERY_KEY, employeeId, accountId, status],
    () =>
      api.get(API_URL.ENROLLMENTS, {
        params: {
          employee_id: employeeId,
          account_id: accountId,
          status: status,
        },
      }),
    {
      enabled: Boolean(employeeId && enabled),
    }
  );
  const currentMyAccount = useMemo(() => {
    const list = data?.data || [];
    const foundItem = list.find((item) => `${item.account_id}` === `${accountId}`);
    if (foundItem) {
      const currentItem: Enrollment = {
        ...(foundItem?.coverage_tier_type
          ? {
              coverageTierType: foundItem?.coverage_tier_type,
            }
          : {}),
        ...(foundItem?.coverage_tier_effective_date
          ? {
              coverageTierEffectiveDate: foundItem?.coverage_tier_effective_date,
            }
          : {}),
        ...(typeof foundItem?.per_pay_employee === 'number'
          ? {
              perPayEmployee: foundItem.per_pay_employee,
            }
          : {}),
        ...(typeof foundItem?.per_pay_employer === 'number'
          ? {
              perPayEmployer: foundItem.per_pay_employer,
            }
          : {}),
        id: `${foundItem.account_id || ''}`,
        name: foundItem.enrollment_title || '',
        available: foundItem.available_amount || 0,
        election: foundItem.election_amount || 0,
        CIPStatus: foundItem.cip_status as unknown as CIPStatus,
        isCIPRequired: foundItem.cip_required,
        enrollmentStatus: foundItem?.status as unknown as EnrollmentStatus,
        // status: foundItem?.status,
        accountType: foundItem.account_type as CardTypesEnum,
        allowInvestments: foundItem.allow_investments || false,
        accountStatus: foundItem.account_status as unknown as AccountStatus,
        investmentThreshold: foundItem.investment_threshold || 0,
        investmentOptionTypes: foundItem.investment_option_types as unknown as InvestmentPathType[],
        allowIndividualContributions: foundItem?.allow_individual_contributions,
        displayName: foundItem.display_name,
        minimumInvestmentAmount: foundItem.minimum_investment_amount,
        // TODO: this field is absent in dto
        // @ts-ignore
        accountNumber: foundItem.account_number,
        planId: `${foundItem.plan_id || ''}`,
        policyId: `${foundItem.policy_id || ''}`,
        isRollover: foundItem.is_rollover,
        remainingRollover: Math.min(
          foundItem.available_amount || 0,
          foundItem?.current_rollover_remaining || foundItem?.prior_rollover_remaining || 0
        ),
        remainingRolloverDate: foundItem?.remaining_rollover_balance_date
          ? dayjs(foundItem.remaining_rollover_balance_date).toDate()
          : undefined,
        spendPeriodType: foundItem.spend_period_type as unknown as SpendPeriodType,
        // @ts-ignore
        coveragePeriods: foundItem?.coverage_periods?.map((item) => ({
          startDate: (item?.coverage_date_start
            ? dayjs(onlyDateFromServer(item?.coverage_date_start as unknown as string)).format(
                DEFAULT_DATE_FORMAT
              )
            : NO_DEADLINE_LABEL) as string,
          endDate: item?.coverage_date_end
            ? dayjs(onlyDateFromServer(item?.coverage_date_end as unknown as string)).format(
                DEFAULT_DATE_FORMAT
              )
            : NO_DEADLINE_LABEL,
          limitedPeriods: item?.limited_periods?.map((limitedPeriod) => ({
            limitedPeriodStart: limitedPeriod?.limited_period_start
              ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_start as unknown as string)).format(
                  DEFAULT_DATE_FORMAT
                )
              : null,
            limitedPeriodEnd: limitedPeriod?.limited_period_end
              ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_end as unknown as string)).format(
                  DEFAULT_DATE_FORMAT
                )
              : null,
          })),
        })),
        coverageDateStart: foundItem?.coverage_periods?.[foundItem?.coverage_periods?.length - 1]
          ?.coverage_date_start
          ? dayjs(
              onlyDateFromServer(
                (foundItem?.coverage_periods?.[foundItem?.coverage_periods?.length - 1]
                  ?.coverage_date_start || '') as unknown as string
              )
            ).format(DEFAULT_DATE_FORMAT)
          : '',
        coverageDateEnd: foundItem?.coverage_periods?.[0]?.coverage_date_end
          ? dayjs(
              onlyDateFromServer(foundItem?.coverage_periods?.[0]?.coverage_date_end as unknown as string)
            ).format(DEFAULT_DATE_FORMAT)
          : '',
        submitClaimsDateEnd: foundItem?.submit_claims_date_end
          ? dayjs(onlyDateFromServer(foundItem.submit_claims_date_end as unknown as string)).format(
              DEFAULT_DATE_FORMAT
            )
          : '',
        deductibleMet: foundItem?.deductible_met,
        contributionMonitoringRequired: foundItem?.contribution_monitoring_required,
        // TODO: this field is absent in DTO
        // @ts-ignore
        isAllowContributionsOverElectionAmount: foundItem?.is_allow_contributions_over_election_amount,
        // @ts-ignore
        deductibleMetDate: foundItem?.deductible_met_date
          ? dayjs(onlyDateFromServer(foundItem.deductible_met_date as unknown as string)).format(
              DEFAULT_DATE_FORMAT
            )
          : null,
        planValidFrom: foundItem?.plan_valid_from as unknown as string,
        electionPeriods: foundItem?.election_periods?.map((item) => ({
          id: _.toString(item.id),
          policyId: _.toString(item.policy_id),
          amount: item.amount || 0,
          validFrom: item.valid_from
            ? dayjs(onlyDateFromServer(item.valid_from as unknown as string)).format(DEFAULT_DATE_FORMAT)
            : NO_DEADLINE_LABEL,
          validTo: item.valid_to
            ? dayjs(onlyDateFromServer(item.valid_to as unknown as string)).format(DEFAULT_DATE_FORMAT)
            : NO_DEADLINE_LABEL,
        })),
      };
      return currentItem;
    }
    return null;
  }, [data, accountId]) as Enrollment;
  const HSA_TYPE_KEY = 'HSA';
  // const currentMyAccount: RawEnrollment | null | undefined = useMemo(
  //   () => {
  //     const list = data?.data || [];
  //     const item = list.find((item) => `${item.account_id}` === id);
  //     if(item) {
  //       // return {
  //       //   id: item.account_id,
  //       //   name: item.enrollment_title,
  //       //   available: item.available_amount,
  //       //   election: item.election_amount,
  //       //   accountType: item.account_type,
  //       // }
  //     }
  //     return null;
  //   }), [data, id],
  // );

  const isHSAExists = useMemo(() => {
    const list = data?.data || [];
    return list.find((item) => item.account_type === HSA_TYPE_KEY);
  }, [data]);
  const isActiveHSAExists = useMemo(
    () =>
      data?.data?.some(
        (item) => item.account_type === HSA_TYPE_KEY && item.account_status === OPEN_ACCOUNT_STATUS
      ),
    [data]
  );
  const isCurrentHSA = useMemo(() => {
    if (currentMyAccount) {
      return currentMyAccount.accountType === HSA_TYPE_KEY;
    }
    return false;
  }, [currentMyAccount]);

  const formattedData = useMemo(
    () =>
      data?.data?.map((item) => {
        const currentItem: Enrollment = {
          ...(item?.coverage_tier_type
            ? {
                coverageTierType: item?.coverage_tier_type,
              }
            : {}),
          ...(item?.coverage_tier_effective_date
            ? {
                coverageTierEffectiveDate: item?.coverage_tier_effective_date,
              }
            : {}),
          ...(typeof item?.per_pay_employer === 'number'
            ? {
                perPayEmployer: item.per_pay_employer,
              }
            : {}),
          ...(typeof item?.per_pay_employee === 'number'
            ? {
                perPayEmployee: item.per_pay_employee,
              }
            : {}),
          id: `${item.account_id || ''}`,
          name: item.enrollment_title || '',
          available: item.available_amount || 0,
          election: item.election_amount || 0,
          CIPStatus: item.cip_status as unknown as CIPStatus,
          isCIPRequired: item.cip_required,
          accountType: item.account_type as CardTypesEnum,
          allowInvestments: item.allow_investments || false,
          accountStatus: item.account_status as unknown as AccountStatus,
          enrollmentStatus: item?.status as unknown as EnrollmentStatus,
          investmentThreshold: item.investment_threshold || 0,
          investmentOptionTypes: item.investment_option_types as unknown as InvestmentPathType[],
          displayName: item.display_name,
          // TODO: this fields is not present in DTO
          // @ts-ignore
          accountNumber: item.account_number,
          enrollmentTitle: item.enrollment_title,
          planId: `${item.plan_id || ''}`,
          policyId: `${item.policy_id || ''}`,
          isRollover: item.is_rollover,
          deductibleMet: item.deductible_met,

          remainingRollover: Math.min(
            item.available_amount || 0,
            item?.current_rollover_remaining || item?.prior_rollover_remaining || 0
          ),
          remainingRolloverDate: item?.remaining_rollover_balance_date
            ? dayjs(item.remaining_rollover_balance_date).toDate()
            : undefined,
          spendPeriodType: item?.spend_period_type as unknown as SpendPeriodType,
          // @ts-ignore
          coveragePeriods: item?.coverage_periods?.map((item) => ({
            startDate: item?.coverage_date_start
              ? dayjs(onlyDateFromServer(item?.coverage_date_start as unknown as string)).format(
                  DEFAULT_DATE_FORMAT
                )
              : NO_DEADLINE_LABEL,
            endDate: item?.coverage_date_end
              ? dayjs(onlyDateFromServer(item?.coverage_date_end as unknown as string)).format(
                  DEFAULT_DATE_FORMAT
                )
              : NO_DEADLINE_LABEL,
            limitedPeriods: item?.limited_periods?.map((limitedPeriod) => ({
              limitedPeriodStart: limitedPeriod?.limited_period_start
                ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_start as unknown as string)).format(
                    DEFAULT_DATE_FORMAT
                  )
                : null,
              limitedPeriodEnd: limitedPeriod?.limited_period_end
                ? dayjs(onlyDateFromServer(limitedPeriod?.limited_period_end as unknown as string)).format(
                    DEFAULT_DATE_FORMAT
                  )
                : null,
            })),
          })),
          coverageDateStart: item?.coverage_periods?.[item?.coverage_periods?.length - 1]?.coverage_date_start
            ? dayjs(
                onlyDateFromServer(
                  (item.coverage_periods?.[item?.coverage_periods?.length - 1]?.coverage_date_start ||
                    '') as unknown as string
                )
              ).format(DEFAULT_DATE_FORMAT)
            : '',
          coverageDateEnd: item?.coverage_periods?.[0]?.coverage_date_end
            ? dayjs(
                onlyDateFromServer(item.coverage_periods?.[0]?.coverage_date_end as unknown as string)
              ).format(DEFAULT_DATE_FORMAT)
            : '',
          submitClaimsDateEnd: item?.submit_claims_date_end
            ? dayjs(onlyDateFromServer(item.submit_claims_date_end as unknown as string)).format(
                DEFAULT_DATE_FORMAT
              )
            : '',
          planValidFrom: item?.plan_valid_from as unknown as string,
          // @ts-ignore
          electionPeriods: item?.election_periods?.map((period) => ({
            id: _.toString(period.id),
            policyId: _.toString(period.policy_id),
            amount: period.amount || '',
            validFrom: period.valid_from
              ? dayjs(onlyDateFromServer(period.valid_from as unknown as string)).format(DEFAULT_DATE_FORMAT)
              : NO_DEADLINE_LABEL,
            validTo: period.valid_to
              ? dayjs(onlyDateFromServer(period.valid_to as unknown as string)).format(DEFAULT_DATE_FORMAT)
              : NO_DEADLINE_LABEL,
          })),
        };
        return currentItem;
      }),
    [data]
  ) as Enrollment[];

  return {
    data: formattedData || [],
    isHSAExists,
    isActiveHSAExists,
    currentMyAccount,
    error,
    isCurrentHSA,
    isLoading,
    isSuccess,
    refetch,
  };
};
